import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

async function cropToBlob(src, area, size = 1000) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
}

export default function PhotoCropper({ file, onDone, onCancel }) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const apply = async () => {
    if (!area) return;
    setBusy(true);
    const blob = await cropToBlob(src, area);
    URL.revokeObjectURL(src);
    onDone(new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent data-testid="photo-cropper-modal" className="max-w-lg bg-slate-950 border-slate-800 text-white">
        <DialogHeader><DialogTitle className="display uppercase">Crop & center photo</DialogTitle></DialogHeader>
        <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-slate-900">
          <Cropper image={src} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} showGrid={false} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-slate-500 w-12">Zoom</span>
          <Slider data-testid="photo-cropper-zoom" min={1} max={3} step={0.01} value={[zoom]} onValueChange={([z]) => setZoom(z)} className="flex-1" />
        </div>
        <p className="text-xs text-slate-500">Drag to reposition. The square area is what will appear on the site.</p>
        <div className="flex justify-end gap-2">
          <Button data-testid="photo-cropper-cancel" type="button" variant="outline" className="border-slate-700 text-slate-200" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button data-testid="photo-cropper-apply" type="button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold" onClick={apply} disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Use this photo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
